import * as signalR from '@microsoft/signalr'

export type UserPresencePayload = {
  orgUserId: number
  isOnline: boolean
  connectedOnUtc?: string | null
  lastLeaveUtc?: string | null
  fullName?: string | null
  email?: string | null
}

type PresenceHandler = (payload: UserPresencePayload) => void

let connection: signalR.HubConnection | null = null
let presenceHandlers: PresenceHandler[] = []

const HUB_PATH = '/SSExpensesHub'
const PRESENCE_EVENT = 'UserPresenceChanged'
const PRESENCE_SNAPSHOT_EVENT = 'UserPresenceSnapshot'

export const subscribeUserPresence = (handler: PresenceHandler) => {
  presenceHandlers.push(handler)
  return () => {
    presenceHandlers = presenceHandlers.filter((h) => h !== handler)
  }
}

const notifyPresence = (payload: UserPresencePayload) => {
  if (!payload?.orgUserId) return
  presenceHandlers.forEach((h) => {
    try {
      h(payload)
    } catch {
      /* ignore handler errors */
    }
  })
}

const normalizePresence = (raw: any): UserPresencePayload | null => {
  const orgUserId = Number(raw?.orgUserId ?? raw?.OrgUserId ?? 0)
  if (!orgUserId) return null
  return {
    orgUserId,
    isOnline: !!(raw?.isOnline ?? raw?.IsOnline),
    connectedOnUtc: raw?.connectedOnUtc ?? raw?.ConnectedOnUtc ?? null,
    lastLeaveUtc: raw?.lastLeaveUtc ?? raw?.LastLeaveUtc ?? null,
    fullName: raw?.fullName ?? raw?.FullName ?? null,
    email: raw?.email ?? raw?.Email ?? null,
  }
}

const buildHubUrl = (baseURL: string, opts: {
  orgUserId?: number | null
  orgId?: number | null
  roleId?: number | null
  email?: string | null
  fullName?: string | null
}) => {
  const params = new URLSearchParams()
  if (opts.orgUserId) params.set('orgUserId', String(opts.orgUserId))
  if (opts.orgId) params.set('orgId', String(opts.orgId))
  if (opts.roleId) params.set('roleId', String(opts.roleId))
  if (opts.email) params.set('email', opts.email)
  if (opts.fullName) params.set('fullName', opts.fullName)
  const qs = params.toString()
  const root = `${baseURL.replace(/\/$/, '')}${HUB_PATH}`
  return qs ? `${root}?${qs}` : root
}

export const startSSExpensesHub = async (opts: {
  baseURL?: string | null
  token?: string | null
  orgUserId?: number | null
  orgId?: number | null
  roleId?: number | null
  email?: string | null
  fullName?: string | null
}) => {
  const { baseURL, token } = opts
  if (!baseURL || !token) return null

  if (connection) {
    if (connection.state === signalR.HubConnectionState.Connected) return connection
    try {
      await connection.stop()
    } catch {
      /* ignore */
    }
    connection = null
  }

  const hubUrl = buildHubUrl(baseURL, opts)

  connection = new signalR.HubConnectionBuilder()
    .withUrl(hubUrl, {
      accessTokenFactory: () => token,
      transport: signalR.HttpTransportType.WebSockets | signalR.HttpTransportType.LongPolling,
    })
    .withAutomaticReconnect([0, 2000, 5000, 10000, 30000])
    .configureLogging(signalR.LogLevel.Warning)
    .build()

  connection.on(PRESENCE_EVENT, (payload: any) => {
    const normalized = normalizePresence(payload)
    if (normalized) notifyPresence(normalized)
  })

  connection.on(PRESENCE_SNAPSHOT_EVENT, (rows: any) => {
    if (!Array.isArray(rows)) return
    rows.forEach((row) => {
      const normalized = normalizePresence(row)
      if (normalized) notifyPresence(normalized)
    })
  })

  connection.onreconnected(async () => {
    try {
      const rows = await connection?.invoke('GetPresenceSnapshot')
      if (Array.isArray(rows)) {
        rows.forEach((row) => {
          const normalized = normalizePresence(row)
          if (normalized) notifyPresence(normalized)
        })
      }
    } catch {
      /* ignore */
    }
  })

  try {
    await connection.start()
    try {
      const rows = await connection.invoke('GetPresenceSnapshot')
      if (Array.isArray(rows)) {
        rows.forEach((row) => {
          const normalized = normalizePresence(row)
          if (normalized) notifyPresence(normalized)
        })
      }
    } catch {
      /* GetPresenceSnapshot is optional for non-watchers */
    }
  } catch (err) {
    console.warn('SSExpensesHub connect failed', err)
    connection = null
    return null
  }

  return connection
}

export const stopSSExpensesHub = async () => {
  if (!connection) return
  try {
    await connection.stop()
  } catch {
    /* ignore */
  }
  connection = null
}

export const getSSExpensesHubConnection = () => connection
